import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {styled} from '@mui/material/styles';
import Business from '@mui/icons-material/Business';

import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
    TablePagination,
    TextField,
    Menu,
    MenuItem,
    Checkbox,
    ListItemText,
    FormControl,
} from '@mui/material';
import {
    PlayArrow,
    CalendarToday,
    Info,
    FilterList,
    Refresh,
    BarChart,
    KeyboardArrowDown,
} from '@mui/icons-material';
import UserJourneyAnimation from './UserJourneyAnimation';
import UserJourneyStats from './UserJourneyStats';

const baseApiUrl = "https://dtrmn8-management-api-foqz7.ondigitalocean.app";
const ResizableTableCell = styled(TableCell)`
    position: relative;

    &:after {
        content: '';
        position: absolute;
        right: 0;
        top: 0;
        height: 100%;
        width: 4px;
        cursor: col-resize;
        user-select: none;
    }
`;
const TableColumnFilter = ({title, options, selected, onSelect, width}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleOptionClick = (value) => onSelect(value);

    const safeOptions = Array.isArray(options) ? options : [];
    const safeSelected = Array.isArray(selected) ? selected : [];

    return (
        <Box>
            <Box
                onClick={handleClick}
                sx={{display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none'}}
            >
                {title}
                <KeyboardArrowDown
                    sx={{
                        ml: 0.5,
                        color: safeSelected.length > 0 ? 'primary.main' : 'inherit',
                        transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s'
                    }}
                />
                {safeSelected.length > 0 && (
                    <Chip size="small" label={safeSelected.length} color="primary" sx={{ml: 1, height: 20}}/>
                )}
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{sx: {maxHeight: 300, width: width || 'auto', minWidth: 200}}}
            >
                {safeOptions.map((option) => (
                    <MenuItem key={option} onClick={() => handleOptionClick(option)} dense>
                        <Checkbox checked={safeSelected.includes(option)} size="small"/>
                        <ListItemText primary={option}/>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};


const siteLabel = (name) => name.replace(/_/g, '.');

const UserJourneyReport = () => {
    const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    const [endDate, setEndDate] = useState(new Date());

    const [sessionData, setSessionData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedSession, setSelectedSession] = useState(null);
    const [showAnimation, setShowAnimation] = useState(false);
    const [showStats, setShowStats] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(250);
    const [totalSessions, setTotalSessions] = useState(0);

    const [columnWidths, setColumnWidths] = useState({
        sessionId: 180,
        source: 150,
        startTime: 150,
        pageViews: 100,
        userType: 120,
        conversions: 100,
        actions: 120
    });

    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(false);
    const [sitesError, setSitesError] = useState(null);
    const [selectedSite, setSelectedSite] = useState(() => localStorage.getItem('selectedSite') || '');

    const [filters, setFilters] = useState({
        source: [], // ["google / cpc", ...]
        userType: [], // ["high","medium",...]
        pageViews: [], // [1,3,5,...] (server uses >= max)
        conversions: [] // [1,5,10,...] -> server minConv = max(...)
    });

    const [filterOptions, setFilterOptions] = useState({
        sourceMediumPairs: [],
        userTypes: [],
        conversionThresholds: [],
        pageViewThresholds: []
    });

    const fetchFilters = useCallback(async () => {
        try {
            if (!selectedSite) {
                setFilterOptions({
                    sourceMediumPairs: [],
                    userTypes: [],
                    conversionThresholds: [],
                    pageViewThresholds: []
                });
                return;
            }
            const endISO = moment(endDate).endOf('day').toISOString();
            const startISO = moment(startDate).startOf('day').toISOString();

            const {data} = await axios.get(`${baseApiUrl}/api/filters`, {
                params: {startDate: startISO, endDate: endISO, companyCollection: selectedSite},
                timeout: 60000
            });

            if (data?.success) {
                setFilterOptions({
                    sourceMediumPairs: data.sourceMediumPairs || [],
                    userTypes: data.userTypes || [],
                    conversionThresholds: (data.conversionThresholds || []).map(String),
                    pageViewThresholds: (data.pageViewThresholds || []).map(String)
                });
            }
        } catch (e) {
            setFilterOptions({sourceMediumPairs: [], userTypes: [], conversionThresholds: [], pageViewThresholds: []});
        }
    }, [selectedSite, startDate, endDate]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setSitesLoading(true);
                setSitesError(null);
                const res = await axios.get(`${baseApiUrl}/api/collections`);
                const names = (res.data?.collections || []).filter((n) => n && !n.startsWith('system.'));
                if (!active) return;
                setSites(names);

                const saved = localStorage.getItem('selectedSite');
                const next = (saved && names.includes(saved)) ? saved : (names[0] || '');
                if (!selectedSite || !names.includes(selectedSite)) {
                    setSelectedSite(next);
                    if (next) localStorage.setItem('selectedSite', next);
                }
            } catch (e) {
                if (active) setSitesError(e.message || 'Failed to load sites');
            } finally {
                if (active) setSitesLoading(false);
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSiteChange = (evt) => {
        const value = evt.target.value;
        setSelectedSite(value);
        localStorage.setItem('selectedSite', value);
        setPage(0);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (!selectedSite) {
                setSessionData({});
                setTotalSessions(0);
                return;
            }
            const endDateTime = moment(endDate).endOf('day');
            const startDateTime = moment(startDate).startOf('day');
            const formattedStartDate = startDateTime.toISOString();
            const formattedEndDate = endDateTime.toISOString();

            const minConv =
                filters.conversions.length > 0
                    ? Math.max(...filters.conversions.map(v => Number(v) || 0))
                    : undefined;

            // NEW: pass all filter buckets to server
            const params = {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                page,
                limit: rowsPerPage,
                companyCollection: selectedSite,
                ...(minConv ? {minConv} : {}),
                ...(filters.source.length ? {source: filters.source} : {}),
                ...(filters.userType.length ? {userType: filters.userType} : {}),
                ...(filters.pageViews.length ? {pageViews: filters.pageViews.map(Number)} : {})
            };

            const response = await axios.get(`${baseApiUrl}/api/user-actions`, {
                params,
                paramsSerializer: {indexes: null}, // send arrays as repeated params or comma-sep depending on backend; null keeps simple
                timeout: 180000
            });

            if (response.data?.success) {
                setSessionData(response.data.data || {});
                const total = response.data.totalSessions ?? response.data.total ?? 0;
                setTotalSessions(total);

                if (!response.data.data || Object.keys(response.data.data).length === 0) {
                    setError('No data found for the selected date range.');
                }
            } else {
                throw new Error(response.data?.message || 'Failed to fetch data');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setSessionData({});
            setTotalSessions(0);
            let errorMessage = 'An error occurred while fetching data';
            if (err.response) errorMessage = err.response.data?.message || 'Server returned an error';
            else if (err.request) errorMessage = 'Unable to connect to the server. Please check if the server is running.';
            else errorMessage = err.message;
            setError(errorMessage);
        } finally {
            setLoading(false);
        }

    }, [endDate, startDate, page, rowsPerPage, selectedSite, filters.source, filters.userType, filters.pageViews, filters.conversions]);

    useEffect(() => {
        const debugInfo = {
            startDate: moment(startDate).format('MM/DD/YYYY'),
            endDate: moment(endDate).format('MM/DD/YYYY'),
            selectedSite,
            currentTime: new Date().toISOString()
        };
        console.log('Debug Info:', debugInfo);
        fetchData();
        fetchFilters();
    }, [startDate, endDate, selectedSite, page, rowsPerPage, fetchData, fetchFilters]);

    const handlePlayAnimation = (sessionId) => {
        setSelectedSession(sessionId);
        setShowAnimation(true);
        setShowStats(false);
    };
    const handleShowStats = (sessionId) => {
        setSelectedSession(sessionId);
        setShowStats(true);
        setShowAnimation(false);
    };
    const closeAnimation = () => setShowAnimation(false);
    const closeStats = () => setShowStats(false);

    const getSessionSummary = (actions) => {
        if (!actions || actions.length === 0) return {};
        const firstAction = actions[0];
        const lastAction = actions[actions.length - 1];
        const pageViews = actions.filter(a => a.UserActionType === 'page_view').length;
        const conversionCount = actions.reduce((sum, a) => sum + (a.ConversionCount || 0), 0);
        return {
            source: firstAction.SessionFcSource || 'Direct',
            medium: firstAction.SessionFcMedium || 'None',
            campaign: firstAction.SessionFcCampaign || 'None',
            pageViews,
            conversionCount,
            userType: lastAction.NextUserType || 'Unknown',
        };
    };

    const handleChangePage = (_e, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (e) => {
        const newRowsPerPage = parseInt(e.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleColumnResize = (column, width) => {
        setColumnWidths(prev => ({...prev, [column]: width}));
    };

    const datePickerContainerStyle = {
        '& .MuiInputBase-root': {height: 36},
        '& .MuiOutlinedInput-input': {padding: '6px 10px'}
    };

    const handleApplyDateRange = () => {
        setPage(0);
        fetchData();
        fetchFilters();
    };

    const retryFetch = async (attempts = 3) => {
        for (let i = 0; i < attempts; i++) {
            try {
                await fetchData();
                return;
            } catch (err) {
                console.error(`Attempt ${i + 1} failed:`, err);
                if (i === attempts - 1) throw err;
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
            }
        }
    };

    const handleFilterChange = (column, value) => {
        // toggle item in the bucket + reset to first page
        setFilters(prev => {
            const next = {...prev};
            const list = Array.isArray(next[column]) ? next[column] : [];
            const idx = list.indexOf(value);
            next[column] = idx === -1 ? [...list, value] : list.filter(x => x !== value);
            return next;
        });
        setPage(0);
    };

    const getColumnOptions = (column) => {
        switch (column) {
            case 'source':
                return filterOptions.sourceMediumPairs;
            case 'userType':
                return filterOptions.userTypes;
            case 'pageViews':
                return filterOptions.pageViewThresholds;
            case 'conversions':
                return filterOptions.conversionThresholds;
            default:
                return [];
        }
    };

    // Client no longer filters by header filters; the server does.
    // We only keep sorting by page_view count (same as before).
    const renderSortedSessionData = () => {
        return Object.entries(sessionData)
            .sort(([, A], [, B]) => {
                const a = A.filter(x => x.UserActionType === 'page_view').length;
                const b = B.filter(x => x.UserActionType === 'page_view').length;
                return b - a;
            });
    };

    return (
        <Container maxWidth="xl" sx={{py: 4}}>
            <Paper elevation={3}
                   sx={{p: 3, mb: 4, borderRadius: 2, background: 'linear-gradient(to right, #1976d2, #2196f3)'}}>
                <Typography variant="h4" component="h1" gutterBottom sx={{color: 'white'}}>
                    User Journey Visualization Report
                </Typography>
                <Typography variant="subtitle1" sx={{color: 'rgba(255, 255, 255, 0.8)'}}>
                    Interactive visualization of user behavior and session data
                </Typography>
            </Paper>

            <Grid container spacing={3} sx={{mb: 4}}>
                <Grid item xs={12} md={6} sx={{display: 'flex', flex: 1}}>
                    <Card elevation={2} sx={{width: '100%', display: 'flex', flexGrow: 1}}>
                        <CardContent sx={{p: 3, height: '100%', display: 'flex', flexDirection: 'column'}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                <Typography variant="h6" sx={{fontWeight: 500}}>Filters</Typography>
                                <Button variant="outlined" startIcon={<Refresh/>} onClick={fetchData} disabled={loading}
                                        size="small">
                                    Refresh Data
                                </Button>
                                <Button
                                    variant="text"
                                    onClick={() => {
                                        setFilters({source: [], userType: [], pageViews: [], conversions: []});
                                        setPage(0);
                                    }}
                                    size="small"
                                    sx={{ml: 1}}
                                >
                                    Clear Filters
                                </Button>
                            </Box>

                            <Grid container spacing={3} alignItems="end">
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: 'background.paper',
                                        p: 1,
                                        borderRadius: 1
                                    }}>
                                        <Business sx={{mr: 1, color: 'primary.main', fontSize: 20}}/>

                                        <Box>
                                            <Typography variant="caption"
                                                        sx={{mb: 0.5, display: 'block', color: 'text.secondary'}}>
                                                Select Site
                                            </Typography>

                                            <FormControl fullWidth size="small">
                                                <TextField
                                                    select
                                                    value={selectedSite}
                                                    onChange={handleSiteChange}
                                                    size="small"
                                                    sx={{
                                                        maxWidth: 130,
                                                        '& .MuiSelect-select': {
                                                            paddingTop: '6px',
                                                            paddingBottom: '6px',
                                                            minHeight: 'unset',
                                                        },
                                                        '& .MuiInputBase-root': {
                                                            height: 36,
                                                        },
                                                    }}
                                                >
                                                    {sites.map((name) => (
                                                        <MenuItem
                                                            key={name}
                                                            value={name}
                                                            sx={{
                                                                minHeight: 32,
                                                                fontSize: '0.875rem',
                                                                py: 0.5,
                                                            }}
                                                        >
                                                            {siteLabel(name)}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: 'background.paper',
                                        p: 1,
                                        borderRadius: 1
                                    }}>
                                        <CalendarToday sx={{mr: 1, color: 'primary.main', fontSize: 20}}/>
                                        <Box>
                                            <Typography variant="caption"
                                                        sx={{mb: 0.5, display: 'block', color: 'text.secondary'}}>
                                                Start Date
                                            </Typography>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={date => setStartDate(date)}
                                                selectsStart
                                                startDate={startDate}
                                                endDate={endDate}
                                                maxDate={endDate}
                                                showTimeSelect={false}
                                                dateFormat="MM/dd/yyyy"
                                                customInput={
                                                    <TextField size="small" fullWidth sx={{
                                                        ...datePickerContainerStyle,
                                                        '& .MuiInputBase-root': {height: 36}
                                                    }}/>
                                                }
                                            />
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: 'background.paper',
                                        p: 1,
                                        borderRadius: 1
                                    }}>
                                        <CalendarToday sx={{mr: 1, color: 'primary.main', fontSize: 20}}/>
                                        <Box>
                                            <Typography variant="caption"
                                                        sx={{mb: 0.5, display: 'block', color: 'text.secondary'}}>
                                                End Date
                                            </Typography>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={date => setEndDate(date)}
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={startDate}
                                                maxDate={new Date()}
                                                showTimeSelect={false}
                                                dateFormat="MM/dd/yyyy"
                                                customInput={
                                                    <TextField size="small" fullWidth sx={{
                                                        ...datePickerContainerStyle,
                                                        '& .MuiInputBase-root': {height: 36}
                                                    }}/>
                                                }
                                            />
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={handleApplyDateRange}
                                        disabled={loading || !selectedSite}
                                        size="small"
                                        sx={{height: 40}}
                                    >
                                        Apply
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6} sx={{display: 'flex', flex: 1}}>
                    <Card elevation={2} sx={{width: '100%', display: 'flex', flexGrow: 1}}>
                        <CardContent
                            sx={{p: 3, height: '100%', width: '100%', display: 'flex', flexDirection: 'column'}}>
                            <Typography variant="h6" sx={{fontWeight: 500, mb: 3}}>Summary</Typography>
                            <Box sx={{
                                flex: 1,
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}>
                                <Box sx={{
                                    display: 'grid', gap: 2, width: '100%',
                                    '& > div': {
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 1,
                                        width: '100%'
                                    }
                                }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Site</Typography>
                                        <Chip
                                            label={selectedSite ? siteLabel(selectedSite) : '—'}
                                            size="small"
                                            variant="outlined"
                                            sx={{minWidth: 180}}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Date Range</Typography>
                                        <Chip
                                            label={`${moment(startDate).format('MMM DD')} - ${moment(endDate).format('MMM DD, YYYY')}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{minWidth: 180}}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Total Sessions</Typography>
                                        <Chip label={totalSessions.toLocaleString()} size="small" color="secondary"
                                              sx={{minWidth: 100}}/>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Showing Sessions</Typography>
                                        <Chip
                                            label={`${Math.min(rowsPerPage, Object.keys(sessionData).length)} of ${totalSessions}`}
                                            size="small"
                                            variant="outlined"
                                            color="default"
                                            sx={{minWidth: 120}}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Current Page</Typography>
                                        <Chip
                                            label={`${page + 1} of ${Math.ceil(Math.max(totalSessions, 1) / rowsPerPage)}`}
                                            size="small"
                                            variant="outlined"
                                            color="default"
                                            sx={{minWidth: 120}}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                    <CircularProgress/>
                    <Typography variant="body1" sx={{ml: 2}}>Loading session data...</Typography>
                </Box>
            ) : error ? (
                <Paper elevation={2} sx={{p: 3, mb: 4, bgcolor: '#fff9f9', borderLeft: '4px solid #f44336'}}>
                    <Typography color="error" variant="h6">Error Loading Data</Typography>
                    <Typography variant="body1">{error}</Typography>
                    <Box sx={{mt: 2, display: 'flex', gap: 2}}>
                        <Button variant="outlined" color="error" onClick={() => retryFetch()}>Retry</Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setStartDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
                                setEndDate(new Date());
                                retryFetch();
                            }}
                        >
                            Reset Dates
                        </Button>
                    </Box>
                </Paper>
            ) : Object.keys(sessionData).length === 0 ? (
                <Paper elevation={2} sx={{p: 4, textAlign: 'center'}}>
                    <Info sx={{fontSize: 60, color: 'text.secondary', mb: 2}}/>
                    <Typography variant="h6" gutterBottom>No Session Data Available</Typography>
                    <Typography variant="body1" color="textSecondary" paragraph>
                        No user sessions found for the selected date range. Try adjusting your filters.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<FilterList/>}
                        onClick={() => {
                            setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
                            setEndDate(new Date());
                        }}
                    >
                        Expand Date Range
                    </Button>
                </Paper>
            ) : (
                <>
                    <Paper elevation={3} sx={{mb: 4, overflow: 'hidden', borderRadius: 2}}>
                        <Box sx={{p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0'}}>
                            <Typography variant="h6">
                                User Sessions
                                ({Math.min(rowsPerPage, Object.keys(sessionData).length)} of {totalSessions})
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Click the play button to visualize user journey
                            </Typography>
                        </Box>

                        <TableContainer sx={{
                            maxHeight: 600,
                            '& .MuiTableHead-root': {
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                backgroundColor: 'background.paper'
                            }
                        }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <ResizableTableCell style={{width: columnWidths.sessionId}}>Session
                                            ID</ResizableTableCell>
                                        <ResizableTableCell style={{width: columnWidths.source}}>
                                            <TableColumnFilter
                                                title="Source / Medium"
                                                options={getColumnOptions('source')}
                                                selected={filters.source}
                                                onSelect={(v) => handleFilterChange('source', v)}
                                                width={250}
                                            />
                                        </ResizableTableCell>
                                        <ResizableTableCell style={{width: columnWidths.startTime}}>Start
                                            Time</ResizableTableCell>
                                        <ResizableTableCell style={{width: columnWidths.pageViews}}>
                                            <TableColumnFilter
                                                title="Page Views"
                                                options={getColumnOptions('pageViews')}
                                                selected={filters.pageViews}
                                                onSelect={(v) => handleFilterChange('pageViews', v)}
                                                width={150}
                                            />
                                        </ResizableTableCell>
                                        <ResizableTableCell style={{width: columnWidths.userType}}>
                                            <TableColumnFilter
                                                title="User Type"
                                                options={getColumnOptions('userType')}
                                                selected={filters.userType}
                                                onSelect={(v) => handleFilterChange('userType', v)}
                                                width={200}
                                            />
                                        </ResizableTableCell>
                                        <ResizableTableCell style={{width: columnWidths.conversions}}>
                                            <TableColumnFilter
                                                title="Conversions"
                                                options={getColumnOptions('conversions')}
                                                selected={filters.conversions}
                                                onSelect={(v) => handleFilterChange('conversions', v)}
                                                width={150}
                                            />
                                        </ResizableTableCell>
                                        <ResizableTableCell
                                            style={{width: columnWidths.actions}}>Actions</ResizableTableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {renderSortedSessionData().map(([sessionId, actions]) => {
                                        const s = getSessionSummary(actions);
                                        return (
                                            <TableRow key={sessionId} hover>
                                                <TableCell
                                                    sx={{maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                    <Tooltip title={sessionId}>
                                                        <Typography variant="body2"
                                                                    noWrap>{sessionId.substring(0, 12)}...</Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={`${s.source} / ${s.medium}`}
                                                        color={s.source === 'Direct' ? 'default' : 'primary'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {actions.length > 0 ? moment(actions[0].TimeStampDate).format('MMM DD, YYYY HH:mm:ss') : 'N/A'}
                                                </TableCell>
                                                <TableCell>{s.pageViews}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={s.userType}
                                                        color={s.userType === 'high' ? 'success' : s.userType === 'medium' ? 'warning' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>{s.conversionCount}</TableCell>
                                                <TableCell align="center">
                                                    <Box>
                                                        <Tooltip title="Play User Journey">
                                                            <IconButton color="primary"
                                                                        onClick={() => handlePlayAnimation(sessionId)}
                                                                        size="small">
                                                                <PlayArrow/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Session Stats">
                                                            <IconButton color="secondary"
                                                                        onClick={() => handleShowStats(sessionId)}
                                                                        size="small">
                                                                <BarChart/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={totalSessions}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[100, 250, 500]}
                        />
                    </Paper>

                    {showAnimation && selectedSession && (
                        <UserJourneyAnimation
                            sessionData={sessionData[selectedSession]}
                            sessionId={selectedSession}
                            onClose={closeAnimation}
                        />
                    )}

                    {showStats && selectedSession && (
                        <UserJourneyStats
                            sessionData={sessionData[selectedSession]}
                            sessionId={selectedSession}
                            onClose={closeStats}
                        />
                    )}
                </>
            )}
        </Container>
    );
};

export default UserJourneyReport;
